import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LoadingBackdrop from "../../components/LoadingBackdrop.js";
import { useUser } from "../../components/UserContext.js";
import DataBox from "../../components/data-box/DataBox.js";
import ErrorDialog from "../../components/dialogs/ErrorDialog.js";
import { getCharacter, getHost, putHost } from "../../lib/apiConfig.js";
import {
  PAGE_CONTAINER_SX,
  PAGE_TITLE_SX,
  STATS_CHART,
} from "../../lib/constants.js";
import { useFetch } from "../../lib/hooks/useFetch.js";
import { computeStats, getStatPropFromLabel } from "../../lib/stats.js";
import StatsChart from "./components/StatsChart.js";

export default function HostBehaviourPage({ locales }) {
  const { isTrans, runId } = useUser();
  const { state } = useLocation();
  const [hostId, setHostId] = useState(null);
  const [host, setHost] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [character, setCharacter] = useState(null);
  const [selectedStat, setSelectedStat] = useState({ stat: null, value: 0 });
  const [sliderValue, setSliderValue] = useState(0);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingHost } = useFetch({
    endpoint: getHost({ id: hostId, runId }),
    mustFetch: hostId && !host,
    onSuccess: (response) => {
      setHost(response);
      setCharacterId(response?.character?.id);
    },
    onFail: setError,
  });

  const { isFetching: isFetchingCharacter } = useFetch({
    endpoint: getCharacter({ id: characterId, runId }),
    mustFetch: characterId && !character,
    onSuccess: setCharacter,
    onFail: setError,
  });

  const { fetchData: saveHost } = useFetch({
    endpoint: putHost({ id: hostId, runId }),
    onFail: setError,
  });

  useEffect(() => {
    setHostId(state.id);
  }, [state]);

  const stats = useMemo(() => computeStats({ host, locales }), [host, locales]);

  const onSelectStat = useCallback(
    (category) => {
      const stat = getStatPropFromLabel({ category, locales });
      const value = Object.entries(host).find(([key]) => key === stat)?.[1];

      setSelectedStat({ stat, value });
      setSliderValue(value);
    },
    [host, locales]
  );

  return !host || isFetchingHost || isFetchingCharacter ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Typography id="behaviour-header" component="h1" sx={PAGE_TITLE_SX}>
        {host.serial}
      </Typography>
      <Grid container id="behaviour-page">
        <Grid xs={12} smd={4} md={3}>
          <DataBox locales={locales} character={character} host={host} />
        </Grid>
        <Grid xs={12} smd={1} />
        <Grid xs={12} smd={7} md={8} sx={{ mt: { xs: 4, smd: 0 } }}>
          <Box id="behaviour-controls" display="flex" gap={6}>
            <StatsChart
              onSelectStat={onSelectStat}
              selectedStat={selectedStat}
              stats={stats}
            />
            {isTrans ? null : (
              <Box id="behaviour-controls-slider">
                <Slider
                  aria-label={locales["behaviour.slider"]}
                  color="secondary"
                  getAriaValueText={(value) => value}
                  marks
                  max={STATS_CHART.MAX}
                  min={STATS_CHART.MIN}
                  orientation="vertical"
                  step={1}
                  value={sliderValue}
                  valueLabelDisplay="auto"
                  onChange={(event, value) => {
                    setSliderValue(value);
                  }}
                  onChangeCommitted={(event, value) => {
                    setSelectedStat({ ...selectedStat, value });
                    saveHost({
                      id: hostId,
                      serial: host.serial,
                      [selectedStat.stat]: value,
                    });
                    setHost({ ...host, [selectedStat.stat]: value });
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
